import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { unwrapListEnvelope } from '../utils/apiNormalize';
import { parseApiErrorMessage } from '../utils/apiError';
import {
  normalizePhoneInput,
  validateEmailCom,
  validatePhone10Digits,
} from '../utils/formValidation';
import PhoneInput from '../components/PhoneInput';
import PageTemplate from './PageTemplate';
import './ModulePage.css';

function AddStudent() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: '',
    email_id: '',
    phone_number: '',
    course: '',
    password: '',
    confirm_password: '',
    role: 'Student',
    has_purchased: false,
  });
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadCourses() {
      setLoadingCourses(true);
      try {
        const coursesRes = await apiClient.get('/api/courses/');
        if (cancelled) return;

        const coursesList = Array.isArray(coursesRes.data)
          ? coursesRes.data
          : unwrapListEnvelope(coursesRes.data, 'courses');

        setCourses(Array.isArray(coursesList) ? coursesList : []);
      } catch (err) {
        if (!cancelled) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error loading courses:', err);
          }
          setCourses([]);
        }
      } finally {
        if (!cancelled) setLoadingCourses(false);
      }
    }

    loadCourses();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue =
      name === 'phone_number' ? normalizePhoneInput(value) : value;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : nextValue,
    }));
  };

  const validateForm = () => {
    if (!form.full_name.trim()) {
      return 'Full name is required.';
    }
    const emailError = validateEmailCom(form.email_id);
    if (emailError) return emailError;
    const phoneError = validatePhone10Digits(form.phone_number);
    if (phoneError) return phoneError;
    if (!form.course.trim()) {
      return 'Course is required.';
    }
    if (!form.password) {
      return 'Password is required.';
    }
    if (form.password.length < 8) {
      return 'Password must be at least 8 characters long.';
    }
    if (form.password !== form.confirm_password) {
      return 'Passwords do not match.';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        full_name: form.full_name.trim(),
        email_id: form.email_id.trim(),
        phone_number: normalizePhoneInput(form.phone_number),
        course: form.course.trim(),
        password: form.password,
        confirm_password: form.confirm_password,
        role: 'student',
        has_purchased: Boolean(form.has_purchased),
      };

      await apiClient.post('/api/users/', payload);

      setSuccess('Student added successfully.');
      setTimeout(() => navigate('/students', { replace: true }), 600);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error adding student:', err);
      }

      const errorMsg = parseApiErrorMessage(
        err,
        'Unable to add student. Please try again.'
      );
      if (err?.response?.status === 401) {
        setTimeout(() => navigate('/login'), 2000);
      }
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageTemplate
      title="Add Student"
      subtitle="Create a new student account for your LMS"
    >
      <div className="add-teacher-panel">
        <div className="add-teacher-header">
          <div>
            <h2>Student details</h2>
            <p>Fill in the basic information and credentials for the student.</p>
          </div>
          <span className="add-teacher-badge">Role: Student</span>
        </div>

        <form className="add-teacher-grid" onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="full_name">Full Name</label>
            <input
              id="full_name"
              name="full_name"
              type="text"
              placeholder="Enter student name"
              value={form.full_name}
              onChange={handleChange}
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email_id">Email</label>
            <input
              id="email_id"
              name="email_id"
              type="email"
              placeholder="name@example.com"
              value={form.email_id}
              autoComplete="email"
              onChange={handleChange}
              disabled={submitting}
            />
          </div>

          <PhoneInput
            id="phone_number"
            label="Mobile Number"
            value={form.phone_number}
            onChange={(phone) => setForm((prev) => ({ ...prev, phone_number: phone }))}
            required
            disabled={submitting}
          />

          <div className="form-group">
            <label htmlFor="course">Course</label>
            {loadingCourses ? (
              <select className="select-input" disabled>
                <option>Loading courses…</option>
              </select>
            ) : courses.length === 0 ? (
              <>
                <input
                  id="course"
                  name="course"
                  type="text"
                  placeholder="Enter course name"
                  value={form.course}
                  onChange={handleChange}
                  disabled={submitting}
                />
                <small style={{ color: '#6b7280' }}>
                  Unable to load courses. Enter the course name manually.
                </small>
              </>
            ) : (
              <select
                id="course"
                name="course"
                value={form.course}
                onChange={handleChange}
                className="select-input"
                disabled={submitting}
              >
                <option value="">Choose a course</option>
                {courses
                  .filter((course) => (course.course_name || course.name || '').trim())
                  .map((course, index) => {
                    const courseId = course.course_id || course.id || `course-${index}`;
                    const courseName = course.course_name || course.name || '';
                    return (
                      <option key={courseId} value={courseName}>
                        {courseName}
                      </option>
                    );
                  })}
              </select>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              placeholder="Enter password"
              value={form.password}
              onChange={handleChange}
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirm_password">Confirm Password</label>
            <input
              id="confirm_password"
              name="confirm_password"
              type="password"
              placeholder="Re-enter password"
              value={form.confirm_password}
              onChange={handleChange}
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label>Role</label>
            <input type="text" value={form.role} disabled />
          </div>

          <div className="form-group form-group-wide">
            <label className="form-checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="has_purchased"
                checked={form.has_purchased}
                onChange={handleChange}
                disabled={submitting}
              />
              <span>Has purchased course</span>
            </label>
          </div>

          {error && <p className="form-error form-group-wide">{error}</p>}
          {success && <p className="form-success form-group-wide">{success}</p>}

          <div className="add-teacher-actions">
            <button
              type="button"
              className="btn-primary btn-secondary"
              onClick={() => navigate('/students')}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="primary-btn" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save Student'}
            </button>
          </div>
        </form>
      </div>
    </PageTemplate>
  );
}

export default AddStudent;
